import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, Facebook, Twitter, Instagram, Youtube, Mail } from 'lucide-react';

const Footer = () => {
  const [expandedSection, setExpandedSection] = useState('');
  const navigate = useNavigate();

  // Handle navigation with scroll to top
  const handleNavigation = (path) => {
    navigate(path);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Footer links configuration
  const footerLinks = {
    product: [
      { name: 'Quick Stories', href: '/app/tools/quick-story' },
      { name: 'Personalized Storybook', href: '/app/tools/story' },
      { name: 'PhotoBook Magic', href: '/app/tools/photo-books' },
      { name: 'Fable Whisperer', href: '/app/tools/story-narrator' },
    ],
    company: [
      { name: 'About Us', href: '/about-us' },
      { name: 'Contact Us', href: '/contact' },
      { name: 'Privacy Policy', href: '/privacy' },
      { name: 'Terms of Service', href: '/terms-of-service' },
      { name: 'Refund Policy', href: '/refund-policy' },
    ],
    support: [
      { name: 'Help Center', href: '/contact' },
      { name: 'FAQs', href: '/faq' },
      { name: 'Community', href: '/faq' },
      { name: 'Feedback', href: '/contact' },
    ],
    social: [
      { name: 'Facebook', icon: Facebook, href: '#' },
      { name: 'Twitter', icon: Twitter, href: '#' },
      { name: 'Instagram', icon: Instagram, href: '#' },
      { name: 'YouTube', icon: Youtube, href: '#' },
      { name: 'Email', icon: Mail, href: 'mailto:support@kuchipuchi.com' },
    ],
  };

  // Toggle section expansion for mobile
  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? '' : section);
  };

  return (
    <footer className="py-8 md:py-16 bg-[#E1FB5F]">
      <div className="relative">
        {/* Star pattern background */}
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#0F172A_1px,transparent_1px)] bg-[length:16px_16px]"></div>
        
        <div className="relative z-10 mx-auto max-w-7xl px-4 md:px-6 lg:px-8">
          {/* White container with rounded corners */}
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl md:rounded-3xl p-6 md:p-12 mb-8 shadow-xl border border-white/20">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2 md:gap-12">
              {/* Brand Column - Always Expanded */}
              <div className="p-4 md:p-0 space-y-4 border-b md:border-b-0 border-gray-100">
                <div className="flex items-center">
                  <img 
                    src="/images/logo/kuchipuchi_logo1.png" 
                    alt="KuchiPuchi Logo" 
                    className="h-10 md:h-12 w-auto mr-3"
                  />
                  <h3 className="text-xl md:text-2xl font-bold text-[#0F172A]">KuchiPuchi</h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed pr-4">
                Bringing imagination to life with AI-powered storytelling magic. Create, narrate, and share enchanting tales with KuchiPuchi.
                </p>
              </div>

              {/* Product Links - Collapsible on Mobile */}
              <div className="border-b md:border-b-0 border-gray-100">
                <button 
                  onClick={() => toggleSection('product')}
                  className="flex md:hidden items-center justify-between w-full p-4 text-gray-800"
                >
                  <span className="font-semibold">Product</span>
                  <ChevronDown 
                    className={`w-5 h-5 transition-transform ${
                      expandedSection === 'product' ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <div className={`${
                  expandedSection === 'product' ? 'block' : 'hidden'
                } md:block px-4 pb-4 md:p-0`}>
                  <h3 className="hidden md:block text-[#0F172A] font-semibold mb-4">Product</h3>
                  <ul className="space-y-3">
                    {footerLinks.product.map((link) => (
                      <li key={link.name}>
                        <button 
                          onClick={() => handleNavigation(link.href)}
                          className="text-gray-600 hover:text-[#0F172A] transition-colors text-sm block py-1 flex items-center group w-full text-left"
                        >
                          <div className="w-1.5 h-1.5 rounded-full bg-[#0F172A] mr-2 opacity-40 group-hover:opacity-100 transition-opacity"></div>
                          {link.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Company Links - Collapsible on Mobile */}
              <div className="border-b md:border-b-0 border-gray-100">
                <button 
                  onClick={() => toggleSection('company')}
                  className="flex md:hidden items-center justify-between w-full p-4 text-gray-800"
                >
                  <span className="font-semibold">Company</span>
                  <ChevronDown 
                    className={`w-5 h-5 transition-transform ${
                      expandedSection === 'company' ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <div className={`${
                  expandedSection === 'company' ? 'block' : 'hidden'
                } md:block px-4 pb-4 md:p-0`}>
                  <h3 className="hidden md:block text-[#0F172A] font-semibold mb-4">Company</h3>
                  <ul className="space-y-3">
                    {footerLinks.company.map((link) => (
                      <li key={link.name}>
                        <button 
                          onClick={() => handleNavigation(link.href)}
                          className="text-gray-600 hover:text-[#0F172A] transition-colors text-sm block py-1 flex items-center group w-full text-left"
                        >
                          <div className="w-1.5 h-1.5 rounded-full bg-[#0F172A] mr-2 opacity-40 group-hover:opacity-100 transition-opacity"></div>
                          {link.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Support Links - Collapsible on Mobile */}
              <div className="border-b md:border-b-0 border-gray-100">
                <button 
                  onClick={() => toggleSection('support')}
                  className="flex md:hidden items-center justify-between w-full p-4 text-gray-800"
                >
                  <span className="font-semibold">Support</span>
                  <ChevronDown 
                    className={`w-5 h-5 transition-transform ${
                      expandedSection === 'support' ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <div className={`${
                  expandedSection === 'support' ? 'block' : 'hidden'
                } md:block px-4 pb-4 md:p-0`}>
                  <h3 className="hidden md:block text-[#0F172A] font-semibold mb-4">Support</h3>
                  <ul className="space-y-3">
                    {footerLinks.support.map((link) => (
                      <li key={link.name}>
                        <button 
                          onClick={() => handleNavigation(link.href)}
                          className="text-gray-600 hover:text-[#0F172A] transition-colors text-sm block py-1 flex items-center group w-full text-left"
                        >
                          <div className="w-1.5 h-1.5 rounded-full bg-[#0F172A] mr-2 opacity-40 group-hover:opacity-100 transition-opacity"></div>
                          {link.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="text-center text-[#0F172A] relative pt-6 md:pt-0">
            {/* Decorative dots */}
            <div className="hidden md:block absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <div className="flex space-x-2">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="w-2 h-2 rounded-full bg-[#0F172A]/50"></div>
                ))}
              </div>
            </div>
            
            {/* Flex container for copyright and social links */}
            <div className="flex flex-col-reverse md:flex-row items-center justify-between gap-6 mb-4">
              {/* Copyright */}
              <div className="flex items-center justify-center space-x-3">
                <img 
                  src="/images/logo/kuchipuchi_logo1.png" 
                  alt="KuchiPuchi Logo" 
                  className="h-8 md:h-10 w-auto"
                />
                <p className="text-sm md:text-base font-medium">
                  © {new Date().getFullYear()}, KuchiPuchi. All rights reserved.
                </p>
              </div>

              {/* Social Links */}
              <div className="flex items-center gap-3 md:gap-4">
                {footerLinks.social.map((item) => {
                  const Icon = item.icon;
                  return (
                    <a
                      key={item.name}
                      href={item.href}
                      className="text-[#0F172A]/80 hover:text-[#0F172A] transition-colors p-2 hover:bg-[#0F172A]/10 rounded-full"
                      aria-label={item.name}
                    >
                      <Icon className="h-4 w-4 md:h-5 md:w-5" />
                    </a>
                  );
                })}
              </div>
            </div>
            
            {/* AI Disclaimer - In its own container */}
            <div className="w-full text-center">
              <p className="text-xs text-gray-600">
                KuchiPuchi can occasionally make mistakes, so please verify the content.
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
